import React, {
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useCallback,
  useRef,
  KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'role'> {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Modal size variant */
  size?: ModalSize;
  /** Whether to close on overlay click */
  closeOnOverlayClick?: boolean;
  /** Whether to close on escape key */
  closeOnEscape?: boolean;
  /** Whether to show the close button */
  showCloseButton?: boolean;
  /** Custom portal container element */
  portalContainer?: HTMLElement;
  /** Accessible label for the modal */
  ariaLabel?: string;
  /** ID of element that labels the modal */
  ariaLabelledBy?: string;
  /** ID of element that describes the modal */
  ariaDescribedBy?: string;
  /** Initial element to focus when modal opens */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /** Element to return focus to when modal closes */
  finalFocusRef?: React.RefObject<HTMLElement>;
  /** Children content */
  children?: ReactNode;
}

export interface ModalHeaderProps extends HTMLAttributes<HTMLDivElement> {
  /** Header title */
  title?: string;
  /** Header subtitle */
  subtitle?: string;
  /** Show close button in header */
  showCloseButton?: boolean;
  /** Close handler (passed from Modal) */
  onClose?: () => void;
  /** Children content (overrides title/subtitle) */
  children?: ReactNode;
}

export interface ModalBodyProps extends HTMLAttributes<HTMLDivElement> {
  /** Body padding */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Children content */
  children?: ReactNode;
}

export interface ModalFooterProps extends HTMLAttributes<HTMLDivElement> {
  /** Align footer content */
  align?: 'left' | 'center' | 'right' | 'between';
  /** Children content */
  children?: ReactNode;
}

// Size classes for the modal content
const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm w-full',
  md: 'max-w-md w-full',
  lg: 'max-w-lg w-full',
  xl: 'max-w-xl w-full',
  full: 'max-w-[calc(100vw-2rem)] w-full max-h-[calc(100vh-2rem)]',
};

/**
 * Modal component with CGIC brand styling
 *
 * A flexible modal dialog that uses React portals for proper DOM placement.
 * Supports multiple sizes, accessibility features, and keyboard interactions.
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen,
      onClose,
      size = 'md',
      closeOnOverlayClick = true,
      closeOnEscape = true,
      showCloseButton = true,
      portalContainer,
      ariaLabel,
      ariaLabelledBy,
      ariaDescribedBy,
      initialFocusRef,
      finalFocusRef,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Handle escape key
    const handleKeyDown = useCallback(
      (event: KeyboardEvent | globalThis.KeyboardEvent) => {
        if (closeOnEscape && event.key === 'Escape') {
          event.preventDefault();
          onClose();
        }

        // Focus trap
        if (event.key === 'Tab' && modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          );
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (event.shiftKey) {
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement?.focus();
            }
          } else {
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement?.focus();
            }
          }
        }
      },
      [closeOnEscape, onClose]
    );

    // Handle overlay click
    const handleOverlayClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) {
          onClose();
        }
      },
      [closeOnOverlayClick, onClose]
    );

    // Manage focus and body scroll
    useEffect(() => {
      if (isOpen) {
        // Store the currently focused element
        previousActiveElement.current = document.activeElement as HTMLElement;

        // Capture finalFocusRef.current for cleanup (ref may change before unmount)
        const finalFocusElement = finalFocusRef?.current;
        const previousFocusElement = previousActiveElement.current;

        // Prevent body scroll
        document.body.style.overflow = 'hidden';

        // Add escape key listener
        const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
          handleKeyDown(event);
        };
        document.addEventListener('keydown', handleGlobalKeyDown);

        // Focus the initial element or the modal itself
        requestAnimationFrame(() => {
          if (initialFocusRef?.current) {
            initialFocusRef.current.focus();
          } else if (modalRef.current) {
            // Focus the first focusable element or the modal itself
            const firstFocusable = modalRef.current.querySelector<HTMLElement>(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
              firstFocusable.focus();
            } else {
              modalRef.current.focus();
            }
          }
        });

        return () => {
          document.body.style.overflow = '';
          document.removeEventListener('keydown', handleGlobalKeyDown);

          // Return focus to the previous element (use captured refs)
          if (finalFocusElement) {
            finalFocusElement.focus();
          } else if (previousFocusElement) {
            previousFocusElement.focus();
          }
        };
      }
    }, [isOpen, initialFocusRef, finalFocusRef, handleKeyDown]);

    if (!isOpen) return null;

    const overlayClasses = [
      'fixed inset-0 z-[1100]',
      'flex items-center justify-center',
      'bg-black/50 backdrop-blur-sm',
      'animate-fade-in',
      'p-4',
    ].join(' ');

    const contentClasses = [
      'relative',
      'bg-white rounded-xl shadow-xl',
      'dark:bg-neutral-850 dark:shadow-dark-lg',
      'animate-scale-in',
      'overflow-hidden',
      sizeClasses[size],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const modalContent = (
      <div
        className={overlayClasses}
        onClick={handleOverlayClick}
        aria-hidden="true"
      >
        <div
          ref={(node) => {
            // Handle both refs
            (modalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          tabIndex={-1}
          className={contentClasses}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {showCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className={[
                'absolute top-3 right-3 z-10',
                'p-1.5 rounded-lg',
                'text-neutral-400 hover:text-neutral-600',
                'hover:bg-neutral-100 active:bg-neutral-200',
                'dark:text-neutral-500 dark:hover:text-neutral-300',
                'dark:hover:bg-neutral-800 dark:active:bg-neutral-700',
                'transition-colors duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              ].join(' ')}
              aria-label="Close modal"
            >
              <CloseIcon />
            </button>
          )}
          {children}
        </div>
      </div>
    );

    const container = portalContainer || document.body;
    return createPortal(modalContent, container);
  }
);

Modal.displayName = 'Modal';

/**
 * Modal Header component
 */
export const ModalHeader = forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ title, subtitle, showCloseButton = false, onClose, className = '', children, ...props }, ref) => {
    const baseClasses = [
      'px-6 py-4 border-b border-neutral-200',
      'dark:border-neutral-700',
    ];

    const combinedClassName = [...baseClasses, className].join(' ');

    if (children) {
      return (
        <div ref={ref} className={combinedClassName} {...props}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">{children}</div>
            {showCloseButton && onClose && (
              <button
                type="button"
                onClick={onClose}
                className={[
                  'flex-shrink-0 p-1 rounded-lg',
                  'text-neutral-400 hover:text-neutral-600',
                  'hover:bg-neutral-100 active:bg-neutral-200',
                  'dark:text-neutral-500 dark:hover:text-neutral-300',
                  'dark:hover:bg-neutral-800 dark:active:bg-neutral-700',
                  'transition-colors duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                ].join(' ')}
                aria-label="Close modal"
              >
                <CloseIcon />
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div ref={ref} className={combinedClassName} {...props}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 pr-8">
            {title && (
              <h2 className="font-display font-semibold text-lg text-neutral-900 dark:text-white">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                {subtitle}
              </p>
            )}
          </div>
          {showCloseButton && onClose && (
            <button
              type="button"
              onClick={onClose}
              className={[
                'flex-shrink-0 p-1 rounded-lg',
                'text-neutral-400 hover:text-neutral-600',
                'hover:bg-neutral-100 active:bg-neutral-200',
                'dark:text-neutral-500 dark:hover:text-neutral-300',
                'dark:hover:bg-neutral-800 dark:active:bg-neutral-700',
                'transition-colors duration-200',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              ].join(' ')}
              aria-label="Close modal"
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>
    );
  }
);

ModalHeader.displayName = 'ModalHeader';

/**
 * Modal Body component
 */
export const ModalBody = forwardRef<HTMLDivElement, ModalBodyProps>(
  ({ padding = 'md', className = '', children, ...props }, ref) => {
    const paddingClasses: Record<string, string> = {
      none: '',
      sm: 'px-4 py-3',
      md: 'px-6 py-4',
      lg: 'px-6 py-6',
    };

    const baseClasses = [
      'overflow-y-auto',
      'max-h-[calc(80vh-10rem)]',
    ];

    const combinedClassName = [...baseClasses, paddingClasses[padding], className]
      .filter(Boolean)
      .join(' ');

    return (
      <div ref={ref} className={combinedClassName} {...props}>
        {children}
      </div>
    );
  }
);

ModalBody.displayName = 'ModalBody';

/**
 * Modal Footer component
 */
export const ModalFooter = forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ align = 'right', className = '', children, ...props }, ref) => {
    const baseClasses = [
      'px-6 py-4 border-t border-neutral-200 bg-neutral-50',
      'dark:border-neutral-700 dark:bg-neutral-900/50',
    ];

    const alignClasses: Record<string, string> = {
      left: 'flex justify-start',
      center: 'flex justify-center',
      right: 'flex justify-end',
      between: 'flex justify-between',
    };

    const combinedClassName = [...baseClasses, alignClasses[align], 'gap-3', className].join(' ');

    return (
      <div ref={ref} className={combinedClassName} {...props}>
        {children}
      </div>
    );
  }
);

ModalFooter.displayName = 'ModalFooter';

/** Internal close icon component */
function CloseIcon() {
  return (
    <svg
      className="w-5 h-5"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export default Modal;
